import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Globe, Loader2, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { StatusPill } from "@/components/common";

export default function Domains() {
  const [domains, setDomains] = useState([]);
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/domains").then((r) => setDomains(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/domains", { domain: input.trim() });
      toast.success("Domain added — set your DNS records");
      setInput("");
      await load();
      navigate(`/app/domains/${data.id}`);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this domain and its credentials?")) return;
    try {
      await api.delete(`/domains/${id}`);
      toast.success("Domain removed");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="display text-2xl font-bold">Domains & DNS</h1>
        <p data-testid="domains-auto-check-summary" className="mt-1 text-sm text-slate-400">Pending domains are checked every 15 minutes. Owner notices follow successful DNS verification.</p>
      </div>

      <form onSubmit={add} className="flex flex-col gap-3 rounded-xl border border-slate-800 bg-slate-900/40 p-4 sm:flex-row">
        <div className="relative flex-1">
          <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="domain-add-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="mail.yourcompany.com"
            className="w-full rounded-lg border border-slate-700 bg-slate-950/60 py-2.5 pl-9 pr-3 text-sm outline-none transition-colors focus:border-emerald-500/60" />
        </div>
        <button data-testid="domain-add-submit" disabled={adding} className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-[#04120c] transition-colors hover:bg-emerald-400 disabled:opacity-60">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add domain
        </button>
      </form>

      <div className="space-y-3">
        {domains.length === 0 && <div className="rounded-xl border border-dashed border-slate-800 py-12 text-center text-sm text-slate-500">No domains yet. Add one above to get started.</div>}
        {domains.map((d) => (
          <div key={d.id} data-testid={`domain-row-${d.domain}`} onClick={() => navigate(`/app/domains/${d.id}`)}
            className="group flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900/40 px-5 py-4 transition-colors hover:border-emerald-500/40">
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-emerald-400"><Globe className="h-5 w-5" /></div>
              <div>
                <div className="mono break-all font-medium">{d.domain}</div>
                <div className="text-xs text-slate-500">Added {new Date(d.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <StatusPill status={d.status} testid={`domain-status-${d.domain}`} />
              <button onClick={(e) => remove(d.id, e)} data-testid={`domain-delete-${d.domain}`} className="text-slate-600 transition-colors hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              <ChevronRight className="h-4 w-4 text-slate-600 transition-transform group-hover:translate-x-0.5" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
